/**
 * Policy edit semantics — the single definition shared by server and web.
 *
 * These rules used to live in four places that drifted apart: the server's
 * `assertPolicyContentEditable`, the route's `canEditVisiblePolicy`, and the
 * two status helpers. Opening `pending_approval` for editing took three
 * follow-up fixes precisely because changing one copy left the others
 * enforcing the old rule (the client kept refusing edit mode after the
 * server had already granted the lock). Anything that answers "may this be
 * edited" or "what does editing do to its status" belongs here.
 *
 * Deliberately NOT here:
 *  - view-level concerns (which version is on screen, read-only mode) — see
 *    {@link canEditVisiblePolicyVersion}, which composes this rule with them;
 *  - `shouldWithdrawApprovalOnEdit`, which needs a DB lookup for the
 *    designated approver and so stays server-side. It decides the
 *    `withdrawPendingRequest` argument below.
 */

/** Statuses whose content may not be edited. Empty by design — see below. */
export const NON_EDITABLE_POLICY_STATUSES: readonly string[] = [];

/**
 * Every status is currently editable.
 *
 * - `pending_approval` is editable: blocking it dead-ended a reviewer who
 *   wanted to accept a suggested wording fix, whose only escape was the
 *   approver-only `returnPolicyForChanges` (which needs a written reason).
 *   The safety property lives in {@link computeStatusAfterEdit} instead —
 *   an edit withdraws the request when a distinct approver would otherwise
 *   sign wording they never read.
 * - `approved` is editable: the edit branches a new draft version rather
 *   than mutating signed text.
 *
 * Kept as a named predicate so a future non-editable state has exactly one
 * home, and so both sides keep naming the decision rather than re-deriving
 * it from a status literal.
 */
export function isPolicyStatusEditable(
  status: string | null | undefined,
): boolean {
  if (!status) return true;
  return !NON_EDITABLE_POLICY_STATUSES.includes(status);
}

/**
 * Compose the status rule with the view-level facts only the client knows.
 * Mirrors what the policy route needs so the two cannot drift again.
 *
 * @param status            the policy's current status
 * @param viewingApproved   a historical/approved version is on screen
 * @param currentIsDraft    the current version is an editable draft
 * @param isBlank           no version exists yet (nothing to protect)
 * @param readOnly          the surface itself is read-only (permissions, etc.)
 */
export function canEditVisiblePolicyVersion(params: {
  status: string | null | undefined;
  viewingApproved: boolean;
  currentIsDraft: boolean;
  isBlank: boolean;
  readOnly: boolean;
}): boolean {
  if (params.readOnly) return false;
  if (!isPolicyStatusEditable(params.status)) return false;
  // Viewing an approved/historical version: editing branches from it, so the
  // gate is only "not the signed one".
  if (params.viewingApproved) return false;
  return params.currentIsDraft || params.isBlank;
}

export interface PolicyStatusAfterEdit {
  nextStatus: string;
  /** Clear `approvalRequestedAt` / `approvalRequestedById`. */
  clearRequestFields: boolean;
}

/**
 * What an edit does to a policy's status.
 *
 * @param withdrawPendingRequest only consulted for `pending_approval`.
 *   True when a DISTINCT teammate is the designated approver — they would
 *   otherwise be left with a queue entry pointing at wording they never
 *   read. False for the solo-operator case (the editor IS the approver, or
 *   none is assigned), where forcing a re-request → re-approve round trip
 *   after every wording fix is the busywork `checkBulkPolicyApproveEligibility`
 *   already collapses. Defaults to true so callers without user context
 *   (system / generation paths) stay conservative.
 */
export function computeStatusAfterEdit(
  currentStatus: string,
  hasContent: boolean,
  withdrawPendingRequest = true,
): PolicyStatusAfterEdit {
  if (!hasContent) {
    return { nextStatus: currentStatus, clearRequestFields: false };
  }
  // An approved policy always branches: the edit cannot touch signed text.
  if (currentStatus === "approved") {
    return { nextStatus: "needs_changes", clearRequestFields: true };
  }
  if (currentStatus === "pending_approval") {
    return withdrawPendingRequest
      ? { nextStatus: "generated", clearRequestFields: true }
      : { nextStatus: "pending_approval", clearRequestFields: false };
  }
  return { nextStatus: "generated", clearRequestFields: false };
}
